import { type PrismaClient, Gender_Association } from "@acme/db";
import { getGenderAssociation } from "./getGenderAssociation";

export interface GetModifiersParams {
  /** Tag name(s) to filter by - can be a single string or array of strings */
  tagName?: string | string[];
  /** Category to filter by */
  category?: string;
  /** Maximum number of modifiers to return (defaults to 12) */
  limit?: number | null;
  /** Gender associations to filter by */
  genderAssociation?: string[];
}

export interface GetModifiersOptions {
  /** Order by clause for the query (defaults to { id: 'asc' }) */
  orderBy?: {
    [key: string]: "asc" | "desc";
  };
}

/**
 * Utility function to fetch modifiers from the database with filtering options.
 * This function can be used both inside and outside of tRPC procedures.
 *
 * @param db - The Prisma client instance
 * @param params - Parameters for filtering modifiers
 * @param options - Additional options for the query
 * @returns Promise<Modifier[]> - Array of modifiers matching the criteria
 *
 * @example
 * ```typescript
 * // Get modifiers by tag name
 * const modifiers = await getModifiers(db, { tagName: 'clothing' });
 *
 * // Get modifiers with gender filter
 * const modifiers = await getModifiers(db, {
 *   tagName: ['shirt', 'pants'],
 *   genderAssociation: ['male']
 * });
 *
 * // Get modifiers with custom limit and ordering
 * const modifiers = await getModifiers(db,
 *   { category: 'clothing', limit: 20 },
 *   { orderBy: { tag_name: 'asc' } }
 * );
 * ```
 */
export async function getModifiers(
  db: PrismaClient,
  params: GetModifiersParams,
  options: GetModifiersOptions = {}
) {
  const { tagName, category, limit, genderAssociation } = params;
  const { orderBy = { id: "asc" } } = options;
  const take = limit ?? 12;

  // Build gender filter
  const genderFilter = genderAssociation?.length
    ? {
        gender_association: {
          in: [
            ...genderAssociation.map(getGenderAssociation).filter(Boolean),
            Gender_Association.gender_neutral,
          ],
        },
      }
    : {};

  // Build tag name filter
  const tagNameFilter = tagName
    ? Array.isArray(tagName)
      ? { tag_name: { in: tagName } }
      : { tag_name: tagName }
    : {};

  // Combine all filters
  const where: any = {
    ...tagNameFilter,
    ...(category ? { category } : {}),
    ...genderFilter,
  };

  try {
    const modifiers = await db.modifier.findMany({
      where,
      take,
      orderBy,
    });

    return modifiers;
  } catch (error) {
    // Re-throw with more context
    throw new Error(
      `Failed to fetch modifiers: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}
